import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import { VEHICLE_BRANDS, ELIGIBLE_YEARS } from '../data/vehicleBrands';
import FranchiseInfoNote from '../components/FranchiseInfoNote';

const VehicleInfoPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data from calculator (including carteGriseData for pre-filling)
  const { vehicleData, abonnement, franchise, monthlyPrice, dataSource, carteGriseData } = location.state || {};
  
  // Debug: Log carteGriseData
  useEffect(() => {
    if (carteGriseData) {
      console.log('📋 carteGriseData reçu:', carteGriseData);
      console.log('📅 Date brute:', carteGriseData.first_registration_date);
      console.log('📅 Date alt:', carteGriseData.date_mise_en_circulation);
    }
  }, [carteGriseData]);
  
  // Convert Swiss date format (JJ.MM.AAAA) to HTML date format (YYYY-MM-DD)
  const convertSwissDateToISO = (swissDate) => {
    if (!swissDate) return '';
    // Format: JJ.MM.AAAA (ex: 15.03.2020)
    if (swissDate.includes('.')) {
      const [day, month, year] = swissDate.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // Already in ISO format
    return swissDate;
  };
  
  // Form state - Pre-fill from carteGriseData if available, otherwise from vehicleData
  const [firstRegistrationDate, setFirstRegistrationDate] = useState(() => {
    const date = carteGriseData?.first_registration_date || 
                 carteGriseData?.date_mise_en_circulation || 
                 vehicleData?.first_registration_date || '';
    return convertSwissDateToISO(date);
  });
  const [mileage, setMileage] = useState(
    vehicleData?.mileage ? String(vehicleData.mileage) : ''
  );
  const [plateNumber, setPlateNumber] = useState(
    carteGriseData?.plate_number || vehicleData?.plate_number || ''
  );
  const [vin, setVin] = useState(
    carteGriseData?.vin || vehicleData?.vin || ''
  );
  const [errors, setErrors] = useState({});

  // Editable vehicle identity (in case OCR/homologation returned wrong data)
  const [editVehicle, setEditVehicle] = useState(false);
  const [editedBrand, setEditedBrand] = useState(vehicleData?.brand || '');
  const [editedModel, setEditedModel] = useState(vehicleData?.model || '');
  const [editedYear, setEditedYear] = useState(vehicleData?.year ? String(vehicleData.year) : '');
  
  // Calculate commitment months
  const commitmentMonths = abonnement === 'S' ? 12 : 24;
  
  // Calculate vehicle age and check eligibility
  const [eligibilityErrors, setEligibilityErrors] = useState([]);
  
  const checkEligibility = () => {
    const errors = [];
    
    if (firstRegistrationDate) {
      const registrationYear = new Date(firstRegistrationDate).getFullYear();
      const currentYear = new Date().getFullYear();
      const vehicleAge = currentYear - registrationYear;
      
      // Check age eligibility by plan
      if (abonnement === 'S' && vehicleAge > 20) {
        errors.push({
          field: 'age',
          message: 'Les véhicules âgés de plus de 20 ans ne sont pas éligibles à cet abonnement'
        });
      } else if ((abonnement === 'M' || abonnement === 'L') && vehicleAge > 16) {
        errors.push({
          field: 'age',
          message: 'Les véhicules âgés de plus de 16 ans ne sont pas éligibles à cet abonnement'
        });
      }
    }
    
    if (mileage) {
      const km = parseInt(mileage);
      
      // Check mileage eligibility by plan
      if (abonnement === 'S' && km > 180000) {
        errors.push({
          field: 'mileage',
          message: 'Les véhicules dont le kilométrage dépasse 180 000 km ne sont pas éligibles.'
        });
      } else if ((abonnement === 'M' || abonnement === 'L') && km > 150000) {
        errors.push({
          field: 'mileage',
          message: 'Les véhicules dont le kilométrage dépasse 150 000 km ne sont pas éligibles.'
        });
      }
    }
    
    setEligibilityErrors(errors);
    return errors.length === 0;
  };
  
  // Check eligibility whenever relevant fields change
  useEffect(() => {
    if (firstRegistrationDate || mileage) {
      checkEligibility();
    }
  }, [firstRegistrationDate, mileage, abonnement]);
  
  // Update fields if carteGriseData arrives after component mount
  useEffect(() => {
    if (carteGriseData) {
      // Update date if not already set
      const newDate = carteGriseData.first_registration_date || carteGriseData.date_mise_en_circulation;
      if (newDate && !firstRegistrationDate) {
        const convertedDate = convertSwissDateToISO(newDate);
        console.log('🔄 Mise à jour de la date:', convertedDate);
        setFirstRegistrationDate(convertedDate);
      }
      
      // Update other fields if not already set
      if (carteGriseData.plate_number && !plateNumber) {
        setPlateNumber(carteGriseData.plate_number);
      }
      if (carteGriseData.vin && !vin) {
        setVin(carteGriseData.vin);
      }
    }
  }, [carteGriseData]);
  
  const isEligible = eligibilityErrors.length === 0;
  
  // Redirect if no data
  useEffect(() => {
    if (!vehicleData || !abonnement) {
      navigate('/abonnements');
    }
  }, [vehicleData, abonnement, navigate]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!firstRegistrationDate) {
      newErrors.firstRegistrationDate = 'Date de première mise en circulation requise';
    }
    
    if (!mileage || isNaN(mileage) || parseInt(mileage) < 0) {
      newErrors.mileage = 'Kilométrage valide requis';
    }
    
    if (!plateNumber || plateNumber.length < 2) {
      newErrors.plateNumber = 'Numéro de plaque requis';
    }
    
    // VIN is optional (17 characters if provided)
    if (vin && vin.length !== 17) {
      newErrors.vin = 'VIN doit contenir 17 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Apply manual corrections to vehicle identity if user edited them
      const correctedVehicleData = {
        ...vehicleData,
        vin: vin || vehicleData.vin,
      };
      if (editVehicle) {
        if (editedBrand.trim()) correctedVehicleData.brand = editedBrand.trim();
        correctedVehicleData.model = editedModel.trim() || null;
        const yearNum = parseInt(editedYear, 10);
        if (yearNum && yearNum >= 1980 && yearNum <= new Date().getFullYear()) {
          correctedVehicleData.year = yearNum;
        }
        correctedVehicleData.manually_corrected = true;
      }

      // Navigate to customer info page
      navigate('/subscription/customer-info', {
        state: {
          vehicleData: correctedVehicleData,
          abonnement,
          franchise,
          monthlyPrice,
          firstRegistrationDate,
          mileage: parseInt(mileage),
          plateNumber: plateNumber.toUpperCase(),
          commitmentMonths,
          dataSource,
          carteGriseData
        }
      });
    }
  };
  
  if (!vehicleData) return null;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
      {/* Hero Section */}
      <section className="pt-20 py-16 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Souscription à votre abonnement
          </h1>
          <p className="text-lg text-gray-600">
            Étape 1 sur 3 : Informations véhicule complémentaires
          </p>
        </div>
      </section>

      <div className="container max-w-4xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="flex items-center justify-center mb-8 px-2">
          <div className="flex items-center w-full max-w-md gap-1 sm:gap-2">
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                1
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">Véhicule</span>
            </div>
            <div className="flex-1 h-1 bg-gray-300 min-w-[8px]"></div>
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                2
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-500 truncate">Client</span>
            </div>
            <div className="flex-1 h-1 bg-gray-300 min-w-[8px]"></div>
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                3
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-500 truncate">Récap.</span>
            </div>
          </div>
        </div>

        {/* Vehicle Summary */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-[#52a675]" />
                <h2 className="text-xl font-bold text-gray-900">
                  Véhicule sélectionné
                </h2>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditVehicle((v) => !v)}
                className="text-[#52a675] hover:text-[#3d8a5c]"
                data-testid="edit-vehicle-toggle"
              >
                {editVehicle ? 'Annuler la correction' : 'Corriger ces informations'}
              </Button>
            </div>

            {!editVehicle ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Marque</p>
                    <p className="font-semibold">{vehicleData.brand}</p>
                  </div>
                  {vehicleData.model && (
                    <div>
                      <p className="text-sm text-gray-600">Modèle</p>
                      <p className="font-semibold">{vehicleData.model}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Année</p>
                    <p className="font-semibold">{vehicleData.year}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cylindrée</p>
                    <p className="font-semibold">{vehicleData.displacement_cc} cc</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Puissance</p>
                    <p className="font-semibold">{vehicleData.power_ch} CH</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Abonnement</p>
                    <p className="font-semibold">Plan {abonnement}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Franchise</p>
                    <p className="font-semibold">{franchise} CHF</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Prix mensuel</p>
                    <p className="font-semibold text-[#52a675]">CHF {monthlyPrice?.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3 italic">
                  ⓘ Si les informations affichées ne correspondent pas à votre véhicule (scan carte grise ou recherche par homologation), cliquez sur "Corriger ces informations".
                </p>
                <div className="mt-4">
                  <FranchiseInfoNote abonnement={abonnement} />
                </div>
              </>
            ) : (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg space-y-3">
                <p className="text-sm text-amber-900 font-medium">
                  Corrigez les informations véhicule ci-dessous :
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Marque *</Label>
                    <Select value={editedBrand} onValueChange={setEditedBrand}>
                      <SelectTrigger data-testid="edit-brand-input">
                        <SelectValue placeholder="Sélectionnez une marque" />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {VEHICLE_BRANDS.map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Modèle</Label>
                    <Input
                      value={editedModel}
                      onChange={(e) => setEditedModel(e.target.value)}
                      data-testid="edit-model-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Année *</Label>
                    <Select value={editedYear ? String(editedYear) : ''} onValueChange={(v) => setEditedYear(v)}>
                      <SelectTrigger data-testid="edit-year-input">
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {ELIGIBLE_YEARS.map((y) => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-amber-700">
                  Les autres caractéristiques (cylindrée, puissance, prix) restent inchangées.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commitment Info */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Durée d'engagement minimale :</strong> {commitmentMonths} mois
          </AlertDescription>
        </Alert>
        
        {/* Eligibility Errors */}
        {eligibilityErrors.length > 0 && (
          <div className="mb-6 space-y-3">
            {eligibilityErrors.map((error, index) => (
              <Alert key={index} className="bg-red-50 border-red-300">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 font-medium">
                  {error.message}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Form */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Informations complémentaires du véhicule
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="firstRegistrationDate">
                  Date de première mise en circulation *
                </Label>
                <Input
                  id="firstRegistrationDate"
                  type="date"
                  value={firstRegistrationDate}
                  onChange={(e) => setFirstRegistrationDate(e.target.value)}
                  className={`${errors.firstRegistrationDate ? 'border-red-500' : ''} ${
                    eligibilityErrors.some(e => e.field === 'age') ? 'border-red-500 border-2' : ''
                  }`}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.firstRegistrationDate && (
                  <p className="text-sm text-red-600 mt-1">{errors.firstRegistrationDate}</p>
                )}
              </div>

              <div>
                <Label htmlFor="mileage">
                  Kilométrage actuel (km) *
                </Label>
                <Input
                  id="mileage"
                  type="number"
                  placeholder="Ex: 75000"
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value)}
                  className={`${errors.mileage ? 'border-red-500' : ''} ${
                    eligibilityErrors.some(e => e.field === 'mileage') ? 'border-red-500 border-2' : ''
                  }`}
                  min="0"
                />
                {errors.mileage && (
                  <p className="text-sm text-red-600 mt-1">{errors.mileage}</p>
                )}
              </div>

              <div>
                <Label htmlFor="plateNumber">
                  Numéro de plaque d'immatriculation *
                </Label>
                <Input
                  id="plateNumber"
                  placeholder="Ex: VD 123456"
                  value={plateNumber}
                  onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
                  className={errors.plateNumber ? 'border-red-500' : ''}
                  maxLength={15}
                />
                {errors.plateNumber && (
                  <p className="text-sm text-red-600 mt-1">{errors.plateNumber}</p>
                )}
              </div>

              {/* VIN field - Only show if vehicle was not found by VIN */}
              {dataSource !== 'VIN' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Label htmlFor="vin" className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    Numéro VIN du véhicule *
                  </Label>
                  <Input
                    id="vin"
                    placeholder="Ex: 1HGBH41JXMN109186"
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase().trim())}
                    className={errors.vin ? 'border-red-500 mt-2' : 'mt-2'}
                    maxLength={17}
                  />
                  {errors.vin && (
                    <p className="text-sm text-red-600 mt-1">{errors.vin}</p>
                  )}
                  <p className="text-xs text-yellow-700 mt-2">
                    ℹ️ Le VIN (17 caractères) est requis pour votre contrat. Il se trouve sur la carte grise ou sur le tableau de bord du véhicule.
                  </p>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#52a675] hover:bg-[#4a9566]"
                  disabled={!isEligible}
                >
                  Continuer
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VehicleInfoPage;
